import type { CarState, Control, PhysicsConfig } from "../types";
import { add, clip, cloneVec, dot, headingVector, scale, sub } from "./math";
import type { WorldContact } from "./obstacles";

export interface WallImpact {
  impactSpeed: number;
  impactRatio: number;
  speedBefore: number;
  speedAfter: number;
  headingChange: number;
}

export function updateCar(state: CarState, control: Control, config: PhysicsConfig, dt: number): CarState {
  const steering = clip(control.steering, -1, 1);
  const command = clip(control.throttleBrake, -1, 1);
  let speed = state.speed;
  if (command >= 0) {
    speed += command * config.acceleration * dt;
  } else if (speed > 0) {
    speed = Math.max(0, speed + command * config.braking * dt);
  } else {
    speed += command * config.reverse_acceleration * dt;
  }
  speed *= Math.max(0, 1 - config.drag * dt);
  speed = clip(speed, -config.max_reverse_speed, config.max_forward_speed);
  const speedRatio = Math.abs(speed) / config.max_forward_speed;
  const steeringAuthority = 0.18 + 0.82 * Math.min(1, speedRatio * 2);
  const directionSign = speed >= 0 ? 1 : -1;
  const landingGripSeconds = Math.max(0, state.landingGripSeconds - dt);
  const waterGripSeconds = Math.max(0, state.waterGripSeconds - dt);
  let gripFactor = state.landingGripSeconds > 0 ? Math.max(0.35, 1 - state.landingGripSeconds / 0.7) : 1;
  if (state.waterGripSeconds > 0) {
    const recovery = clip(1 - state.waterGripSeconds / 0.72, 0, 1);
    gripFactor *= state.waterGripFactor + (1 - state.waterGripFactor) * recovery;
  }
  const heading = state.heading + steering * config.steering_rate * steeringAuthority * directionSign * gripFactor * dt;
  const position = add(state.position, scale(headingVector(heading), speed * dt));
  return {
    ...state, position, heading, speed, landingGripSeconds, waterGripSeconds,
    waterGripFactor: waterGripSeconds <= 0 ? 1 : state.waterGripFactor,
  };
}

export function cloneCar(state: CarState): CarState {
  return { ...state, position: cloneVec(state.position) };
}

export function resolveWallContact(state: CarState, contact: WorldContact, config: PhysicsConfig): { car: CarState; impact: WallImpact } {
  const car = cloneCar(state);
  car.position = add(car.position, scale(contact.normal, contact.penetration + 0.05));
  const velocity = scale(headingVector(car.heading), car.speed);
  const inwardComponent = dot(velocity, contact.normal);
  const impactSpeed = Math.max(0, -inwardComponent);
  const tangentVelocity = sub(velocity, scale(contact.normal, Math.min(0, inwardComponent)));
  const impactRatio = clip(impactSpeed / Math.max(1, config.max_forward_speed), 0, 1);
  const tangentRetention = 0.94 - 0.24 * impactRatio;
  const reboundVelocity = scale(contact.normal, impactSpeed * (0.05 + 0.05 * impactRatio));
  const responseVelocity = add(scale(tangentVelocity, tangentRetention), reboundVelocity);
  const headingBefore = car.heading;
  const forward = headingVector(car.heading);
  const lateral = forward[0] * responseVelocity[1] - forward[1] * responseVelocity[0];
  car.heading += clip(lateral / 190, -0.38, 0.38);
  car.speed = clip(dot(responseVelocity, headingVector(car.heading)), -config.max_reverse_speed, config.max_forward_speed);
  car.collided = true;
  return {
    car,
    impact: {
      impactSpeed,
      impactRatio,
      speedBefore: state.speed,
      speedAfter: car.speed,
      headingChange: car.heading - headingBefore,
    },
  };
}
